/**
 * Pairing drawer - see snippets/pairing-drawer.liquid
 *
 * Opens a recommended add-on as a clean product page in a drawer: images,
 * price, options, quantity and add to cart, stock, description and
 * specification, rendered for any product by sections/pairing-drawer.liquid.
 *
 * Options work the way Shopify recommends and the product page does
 * (VariantSelects in global.js): a change sends the chosen option values to
 * Shopify as ?option_values=, and what follows the variant is swapped for
 * Shopify's new render of the section.
 *
 * Adding to the cart posts the drawer's own product form to /cart/add.js and
 * fires the theme's ajaxProduct:added, which refreshes and opens the cart
 * drawer exactly as the page's own buy button does.
 *
 * A link can ask for a fixed variant (data-pairing-variant: the fabric
 * sample), or for a page of the shop instead of a product
 * (data-pairing-page: the measuring guide), and names the drawer's top-bar
 * label while it is open (data-pairing-label).
 */
(() => {
  if (customElements.get('pairing-drawer')) return;
  if (typeof HTMLDialogElement === 'undefined' || !('showModal' in HTMLDialogElement.prototype)) return;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const TRIGGER = '[data-pairing-open], [data-pairing-page]';

  /*
   * The frame a page is shown in. Its height follows the page (measured, see
   * #showPage), so the drawer body stays the one thing that scrolls. Scripts
   * never run there: the sandbox has no allow-scripts.
   */
  const FRAME_CSS = 'html,body{height:auto!important;min-height:0!important;margin:0!important}html{overflow:hidden!important}';
  const FRAME_SANDBOX = 'allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation';
  const attr = (value) => String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

  /**
   * The freight class the product's own page sends with it: the checkout
   * prices freight on the cart properties _Oversized_Goods / _Long_Goods.
   * Only variants with custom.shipping_class. The theme's rule
   * (window.HastaShipping.calculate, component-customizer.js, loaded by the
   * layout on every page) is fed what the page feeds it: the variant's Bredd
   * option, which the page's form reads at submit (component-product-form.js),
   * else its title, which the page reads on load (global.js). The same in
   * pdp-accessories.js, for the quick add.
   * @param {{freight?: boolean, width?: string|null, title?: string}} v
   */
  const freightClass = (v) => {
    if (!v || v.freight !== true) return null;
    const calc = (value) => {
      if (value === null || value === undefined || value === '') return null;
      const rule = window.HastaShipping;
      if (rule && typeof rule.calculate === 'function') return rule.calculate(true, value);
      const match = String(value).replace(',', '.').match(/\d+(?:\.\d+)?/);
      const size = match ? parseFloat(match[0]) : 0;
      if (!(size >= 110)) return null;
      return size <= 260 ? 'Oversized_Goods' : 'Long_Goods';
    };
    return calc(v.width) || calc(v.title);
  };

  class PairingDrawer extends HTMLElement {
    #dialog = null;
    #slot = null;
    #label = null;
    #defaultLabel = '';
    #strings = {};
    #cache = new Map();
    // what is open: product:<handle>:<variant> or page:<url>
    #key = null;
    #trigger = null;
    // the product open in the drawer: the request that opened it
    #product = null;
    // the chosen variant as the latest render gave it (data-pairing-current)
    #current = null;
    // counts option changes, so only the answer to the latest one is used
    #selection = 0;
    #frameObserver = null;
    // read once: the theme rewrites the URL to ?variant= on the first variant click
    #country = new URLSearchParams(window.location.search).get('country');

    connectedCallback() {
      this.#dialog = this.querySelector('dialog');
      this.#slot = this.querySelector('[data-pairing-slot]');
      this.#label = this.querySelector('#PairingDrawerLabel');
      this.#defaultLabel = this.#label?.textContent.trim() || '';
      try {
        const raw = JSON.parse(this.querySelector('[data-pairing-strings]')?.textContent || '{}');
        // `t` HTML-escapes translations ("Couldn&#39;t"); the script writes textContent
        const decode = (value) => new DOMParser().parseFromString(value, 'text/html').documentElement.textContent;
        this.#strings = Object.fromEntries(Object.entries(raw).map(([key, value]) => [key, decode(value)]));
      } catch {
        this.#strings = {};
      }

      document.addEventListener('click', this.#onDocumentClick);
      document.addEventListener('pointerover', this.#onIntent, { passive: true });
      document.addEventListener('focusin', this.#onIntent);
      document.addEventListener('touchstart', this.#onIntent, { passive: true });

      this.#dialog.addEventListener('click', this.#onDialogClick);
      this.#dialog.addEventListener('close', this.#onClose);
      this.#dialog.addEventListener('change', this.#onOptionChange);
      this.#dialog.addEventListener('submit', this.#onSubmit);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.#onDocumentClick);
      document.removeEventListener('pointerover', this.#onIntent);
      document.removeEventListener('focusin', this.#onIntent);
      document.removeEventListener('touchstart', this.#onIntent);
      this.#frameObserver?.disconnect();
    }

    /* ---------- opening and closing ---------- */

    #onDocumentClick = (event) => {
      const link = event.target.closest?.(TRIGGER);
      if (!link || event.defaultPrevented) return;
      // a new tab, a download, a middle click: the link does what links do
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!this.#request(link)) return;
      event.preventDefault();
      this.open(link);
    };

    /** Start the fetch the moment a pointer or focus lands on a trigger */
    #onIntent = (event) => {
      const link = event.target.closest?.(TRIGGER);
      const request = link && this.#request(link);
      if (request) this.#load(request).catch(() => {});
    };

    /**
     * What a link asks for. A page only of this shop: another origin would
     * not answer the fetch anyway, and its markup is not ours to show.
     * @param {HTMLElement} link
     */
    #request(link) {
      const page = link.dataset.pairingPage;
      if (page) {
        let url;
        try {
          url = new URL(page, window.location.origin);
        } catch {
          return null;
        }
        if (url.origin !== window.location.origin) return null;
        return { kind: 'page', key: `page:${url.pathname}${url.search}`, url };
      }
      const handle = link.dataset.pairingOpen;
      if (!handle) return null;
      const variant = link.dataset.pairingVariant || '';
      return { kind: 'product', key: `product:${handle}:${variant}`, handle, variant };
    }

    /**
     * @param {HTMLElement} trigger - the link: what it asks for, and where focus returns on close
     */
    async open(trigger) {
      const request = this.#request(trigger);
      if (!request) return;
      this.#key = request.key;
      this.#trigger = trigger;
      this.#product = request.kind === 'product' ? request : null;
      this.#current = null;
      this.#selection += 1;
      this.#frameObserver?.disconnect();
      this.#frameObserver = null;

      // the link names what it opens; one without a label keeps the drawer's own
      if (this.#label) {
        const label = trigger.dataset.pairingLabel ?? this.#defaultLabel;
        this.#label.textContent = label;
        this.#label.hidden = !label;
      }

      this.#slot.setAttribute('aria-busy', 'true');
      this.#slot.replaceChildren(this.#skeleton());
      this.#slot.scrollTop = 0;

      if (!this.#dialog.open) {
        this.#lockScroll();
        this.#dialog.showModal();
        // preventScroll: at this moment the panel sits off-screen for its slide-in,
        // and a scrolling focus scrolled the dialog 480px to "reveal" the button -
        // cancelling the slide in Chrome and making Safari jump left, then right
        this.#dialog.querySelector('[data-pairing-close]')?.focus({ preventScroll: true });
      }

      try {
        const html = await this.#load(request);
        if (this.#key !== request.key) return; // another row was opened meanwhile
        if (request.kind === 'page') {
          await this.#showPage(html, request);
        } else {
          this.#showProduct(html, request);
        }
      } catch {
        if (this.#key !== request.key) return;
        this.#slot.replaceChildren(this.#loadError(request));
      } finally {
        this.#slot.setAttribute('aria-busy', 'false');
      }
    }

    #showProduct(html, request) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const item = doc.querySelector('[data-pairing-item]');
      // a handle that no longer resolves still answers 200, with the section
      // rendered around an empty product: no variant means nothing to show
      const current = this.#readCurrent(item);
      if (!item || !current) throw new Error('empty');
      // a fixed variant (data-pairing-variant, the fabric sample): its options
      // stay as text, without the choices (sections/pairing-drawer.liquid)
      if (request.variant) item.querySelector('[data-pairing-options]')?.remove();
      this.#slot.replaceChildren(document.adoptNode(item));
      this.#current = current;
    }

    /** The variant a render is for (data-pairing-current); null when the chosen values have none */
    #readCurrent(root) {
      try {
        return JSON.parse(root?.querySelector('[data-pairing-current]')?.textContent || 'null');
      } catch {
        return null;
      }
    }

    /**
     * A page of the shop - the measuring guide - as it looks on a phone: its
     * sections and the page's own stylesheets in a frame as wide as the
     * panel, so the page's phone layout applies at any screen width and
     * nothing of it reaches the product page, or the other way round.
     * Left out: scripts, the hero banner (a phone-height block; its heading
     * becomes the drawer's title), product sliders, and sections that are
     * empty without their scripts.
     */
    async #showPage(html, request) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const main = doc.querySelector('#MainContent');
      if (!main) throw new Error('empty');
      for (const node of main.querySelectorAll('script, noscript')) node.remove();

      let title = '';
      for (const section of main.querySelectorAll('.shopify-section')) {
        const type = section.id.split('__').pop() || '';
        if (type.startsWith('hero')) {
          title ||= section.querySelector('h1, h2')?.textContent.trim() || '';
          section.remove();
          continue;
        }
        // its text without the CSS of its <style> tags: a spacer is only that
        const text = section.cloneNode(true);
        for (const node of text.querySelectorAll('style')) node.remove();
        const empty = !text.textContent.trim() && !section.querySelector('img, video, iframe');
        if (empty || section.querySelector('.card--product')) section.remove();
      }
      if (!main.querySelector('.shopify-section')) throw new Error('empty');
      title ||= doc.querySelector('meta[property="og:title"]')?.content?.trim() || doc.title.trim();

      // the page's stylesheets; the late ones are switched on here, as their
      // onload (which never runs in the sandbox) would have done
      const styles = [];
      for (const node of doc.head.querySelectorAll('link[rel="stylesheet"], link[rel="preload"][as="style"], style')) {
        if (node.tagName === 'STYLE') {
          styles.push(node.outerHTML);
        } else if (node.getAttribute('href')) {
          const media = node.getAttribute('media');
          const late = node.getAttribute('rel') === 'preload' || (media === 'print' && node.hasAttribute('onload'));
          styles.push(`<link rel="stylesheet" href="${attr(node.getAttribute('href'))}" media="${attr(late ? 'all' : media || 'all')}">`);
        }
      }

      const wrap = document.createElement('div');
      wrap.className = 'rd-pair-page is-loading';
      const heading = document.createElement('h2');
      heading.className = 'rd-pair-page__title';
      heading.id = 'PairingDrawerTitle';
      heading.textContent = title;
      const frame = document.createElement('iframe');
      frame.className = 'rd-pair-page__frame';
      frame.title = title;
      frame.setAttribute('sandbox', FRAME_SANDBOX);
      frame.srcdoc =
        `<!doctype html><html lang="${attr(doc.documentElement.lang || document.documentElement.lang)}" class="${attr(doc.documentElement.className)}">` +
        `<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><base target="_top">` +
        `${styles.join('')}<style>${FRAME_CSS}</style></head>` +
        `<body class="${attr(doc.body.className)}">${main.innerHTML}</body></html>`;
      const full = document.createElement('a');
      full.className = 'rd-pair-item__full';
      full.href = request.url.href;
      full.textContent = this.#strings.viewGuide || title;
      full.insertAdjacentHTML('beforeend', '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>');
      wrap.append(heading, frame, full);

      // behind the skeleton until it has loaded and been measured; a slow
      // stylesheet never leaves the skeleton up for good
      await new Promise((resolve) => {
        const timer = setTimeout(resolve, 7000);
        frame.addEventListener('load', () => {
          clearTimeout(timer);
          resolve();
        }, { once: true });
        this.#slot.append(wrap);
      });
      if (this.#key !== request.key) return;

      const inner = frame.contentDocument;
      if (inner?.documentElement) {
        // the root's own height, not scrollHeight: that never drops below the
        // frame's, so a closed accordion would leave the frame tall
        const fit = () => {
          frame.style.height = `${Math.ceil(inner.documentElement.getBoundingClientRect().height)}px`;
        };
        fit();
        this.#frameObserver = new ResizeObserver(fit);
        this.#frameObserver.observe(inner.documentElement);
      }
      wrap.classList.remove('is-loading');
      // the skeleton out, the frame left where it is: an iframe taken out of
      // the document and put back loads again, as a new document
      for (const node of [...this.#slot.children]) {
        if (node !== wrap) node.remove();
      }
    }

    close() {
      if (this.#dialog.open) this.#dialog.close();
    }

    #onDialogClick = (event) => {
      if (event.target === this.#dialog || event.target.closest('[data-pairing-close]')) this.close();
    };

    /**
     * Stop the page scrolling behind the drawer without moving it: with a
     * classic scrollbar (a mouse on macOS, Windows) hiding it widens the page
     * by ~15px and everything centred jumps sideways, then back on close. The
     * page is padded by exactly the scrollbar it loses (see the CSS).
     */
    #lockScroll() {
      const html = document.documentElement;
      html.style.setProperty('--rd-pair-gutter', `${Math.max(0, window.innerWidth - html.clientWidth)}px`);
      html.classList.add('rd-pair-open');
    }

    #onClose = () => {
      const html = document.documentElement;
      html.classList.remove('rd-pair-open');
      html.style.removeProperty('--rd-pair-gutter');
      this.#key = null;
      if (this.#trigger && this.#trigger.isConnected && !this.#dialog.contains(document.activeElement)) {
        this.#trigger.focus({ preventScroll: true });
      }
    };

    /**
     * Section Rendering API: the product rendered by sections/pairing-drawer
     * in its own context, on the asked-for variant when there is one. A page
     * is fetched whole. On the dev server the market only sticks through
     * ?country=, so the parameter the page was opened with is passed along.
     * Live URLs carry none, so nothing changes there.
     */
    #load(request) {
      if (!this.#cache.has(request.key)) {
        let url;
        if (request.kind === 'page') {
          url = new URL(request.url.href);
        } else {
          url = new URL(`${root()}products/${encodeURIComponent(request.handle)}`, window.location.origin);
          url.searchParams.set('section_id', 'pairing-drawer');
          if (request.variant) url.searchParams.set('variant', request.variant);
          if (request.optionValues) url.searchParams.set('option_values', request.optionValues);
        }
        if (this.#country) url.searchParams.set('country', this.#country);
        const pending = fetch(url).then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.text();
        });
        pending.catch(() => this.#cache.delete(request.key));
        this.#cache.set(request.key, pending);
      }
      return this.#cache.get(request.key);
    }

    /* ---------- options ---------- */

    #onOptionChange = (event) => {
      const input = event.target.closest('.rd-pair-opt__input');
      if (!input || !this.#product) return;
      const fieldsets = [...this.#slot.querySelectorAll('[data-pairing-option]')];
      const index = fieldsets.indexOf(input.closest('[data-pairing-option]'));
      // the chosen value of every option, as Shopify's option_values asks for them
      const ids = fieldsets
        .map((fieldset) => fieldset.querySelector('.rd-pair-opt__input:checked')?.dataset.optionValueId)
        .filter(Boolean);
      this.#select(ids, ids.slice(0, index + 1), input.dataset.optionValueId);
    };

    /**
     * Shopify renders the drawer again for the chosen values. When those have
     * no variant between them - a later choice the new value does not come
     * in - only the values up to the one changed are sent, and Shopify picks
     * the first available for the rest, so later choices are kept wherever
     * they still exist, as on the product page.
     */
    async #select(ids, upToChanged, focusId) {
      const selection = (this.#selection += 1);
      const key = this.#key;
      const handle = this.#product.handle;
      // the form still holds the old variant until the new render is in
      const button = this.#slot.querySelector('[data-pairing-add]');
      if (button) button.disabled = true;
      const render = async (values) => {
        const request = { kind: 'options', key: `options:${handle}:${values.join(',')}`, handle, optionValues: values.join(',') };
        return new DOMParser().parseFromString(await this.#load(request), 'text/html');
      };
      try {
        let doc = await render(ids);
        if (!this.#readCurrent(doc) && upToChanged.length < ids.length) doc = await render(upToChanged);
        if (selection !== this.#selection || key !== this.#key) return;
        this.#swap(doc, focusId);
      } catch {
        if (selection !== this.#selection || key !== this.#key) return;
        const error = this.#slot.querySelector('[data-pairing-error]');
        if (error) {
          error.textContent = this.#strings.error || '';
          error.hidden = false;
        }
      }
    }

    /**
     * What follows the variant (data-pairing-region), from the new render: the
     * price, the lowest-price line, the options, the buy row, the stock line,
     * the SKU and the full-page link. The images stay, scrolled to the
     * variant's own; the quantity the shopper set stays too.
     */
    #swap(doc, focusId) {
      const slot = this.#slot;
      const quantity = slot.querySelector('[data-pairing-form] [name="quantity"]')?.value;
      for (const region of slot.querySelectorAll('[data-pairing-region]')) {
        const next = doc.querySelector(`[data-pairing-region="${region.dataset.pairingRegion}"]`);
        if (next) region.replaceWith(document.adoptNode(next));
      }
      const field = slot.querySelector('[data-pairing-form] [name="quantity"]');
      if (field && quantity) field.value = quantity;
      this.#current = this.#readCurrent(slot);
      // the input the shopper used was replaced: focus its new copy
      slot.querySelector(`.rd-pair-opt__input[data-option-value-id="${focusId}"]`)?.focus({ preventScroll: true });
      this.#scrollToMedia(this.#current?.media);
    }

    /**
     * Only after an option change: on open the strip stays where it is -
     * scrolling it there made the images slide sideways while the panel slid in.
     */
    #scrollToMedia(id) {
      if (!id) return;
      const strip = this.#slot.querySelector('[data-pairing-media]');
      const slide = strip?.querySelector(`[data-media-id="${id}"]`);
      // measured from the first slide: the strip's own padding is not a distance
      // to scroll (against the panel edge it read 18px for the first image)
      const first = strip?.querySelector('[data-media-id]');
      // an explicit 'smooth' would override the reduced-motion preference
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      if (slide && first) strip.scrollTo({ left: slide.offsetLeft - first.offsetLeft, behavior });
    }

    /* ---------- add to cart ---------- */

    #onSubmit = async (event) => {
      const form = event.target.closest('[data-pairing-form]');
      if (!form) return;
      event.preventDefault();
      // never a made-to-measure variant - without the customizer's price and
      // measurements it would land in the cart at 0 kr - nor values that have
      // no variant
      const current = this.#current;
      if (!current || current.mtm) return;
      const button = form.querySelector('[data-pairing-add]');
      const error = form.querySelector('[data-pairing-error]');
      if (!button || button.disabled || button.classList.contains('loading')) return;

      error.hidden = true;
      this.#loading(button, true);
      const body = new FormData(form);
      // a typed 0 or a blank adds nothing, yet the cart answers 200 and the
      // drawer would close as if it had worked - so it means 1, as on the
      // theme's own buy button (component-product-form.js); a negative too
      body.set('quantity', String(Math.max(1, parseInt(body.get('quantity'), 10) || 1)));
      // the freight class, as the product's own page would send it
      body.delete('properties[_Oversized_Goods]');
      body.delete('properties[_Long_Goods]');
      const freight = freightClass(current);
      if (freight) body.set(`properties[_${freight}]`, 'Yes');
      try {
        const response = await fetch(`${root()}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok) {
          // the cart's own reason ("Only 2 left in stock"), shown as it is
          const reason = new Error(json.description || json.message || '');
          reason.fromCart = true;
          throw reason;
        }
        this.close();
        // the theme refreshes and opens its cart drawer on this, as for the page's own button
        document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
      } catch (err) {
        // a request that got no answer (offline, blocked) gets our sentence, not
        // the browser's own English "Failed to fetch"
        error.textContent = (err?.fromCart && err.message) || this.#strings.error || '';
        error.hidden = false;
      } finally {
        this.#loading(button, false);
      }
    };

    /** The theme's own loading look: .btn.loading hides the label, the overlay spins */
    #loading(button, on) {
      button.classList.toggle('loading', on);
      button.setAttribute('aria-busy', String(on));
      button.querySelector('.loading-overlay')?.classList.toggle('hidden', !on);
    }

    /* ---------- states ---------- */

    #skeleton() {
      const wrap = document.createElement('div');
      wrap.className = 'rd-pair-skel';
      wrap.setAttribute('aria-hidden', 'true');
      wrap.innerHTML = '<span class="rd-pair-skel__media"></span><span class="rd-pair-skel__line"></span><span class="rd-pair-skel__line rd-pair-skel__line--short"></span>';
      return wrap;
    }

    #loadError(request) {
      const wrap = document.createElement('div');
      wrap.className = 'rd-pair-fail';
      // announced: the dialog is already open and focused when this replaces the placeholder
      wrap.setAttribute('role', 'alert');
      const text = document.createElement('p');
      const link = document.createElement('a');
      if (request.kind === 'page') {
        text.textContent = this.#strings.guideError || '';
        link.href = request.url.href;
        link.textContent = this.#strings.viewGuide || request.url.pathname;
      } else {
        text.textContent = this.#strings.loadError || '';
        link.href = `${root()}products/${encodeURIComponent(request.handle)}`;
        if (request.variant) link.search = `?variant=${encodeURIComponent(request.variant)}`;
        link.textContent = this.#strings.viewProduct || request.handle;
      }
      wrap.append(text, link);
      return wrap;
    }
  }

  customElements.define('pairing-drawer', PairingDrawer);
})();
